import { CanvasService } from '../../services/canvas/canvas.service';
import { ResourceData } from '../../elements/resource/resource.types';
import {
  Component,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  NgZone,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  Coords,
  ExtendedComment,
} from '../../elements/resource/types/shape.type';
import { clone as _cloneDeep, omit as _omit } from 'lodash';

import { RootShape } from '../shape/shapes/general/root/root-shape';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { UntilDestroy } from '@ngneat/until-destroy';
import { GeneralShape } from '../shape/shapes/general/general-shape';
import { ShapeService } from '../shape/shape.service';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  isPatchLoading,
  selectCanvasBackgroundColor,
  selectCanvasPosition,
  selectCommentToShow,
  selectExportIsInProgress,
  selectIsFileLoading,
  selectOverlay,
  selectPreviewShapeLoading,
  showComponentSearch,
} from '../store/selector/editor.selector';
import { ActivatedRoute } from '@angular/router';
import { SVGParseService } from '../svg/svg-parse.service';
import { IRIToId } from '../../util/iri';
import { selectRelativeAnimationTime } from '../animation/store/animation.selector';
import { EditorState } from '../store/reducer/editor.reducer';
import { catchError, map } from 'rxjs/operators';
import { openCanvasBgColorEdit } from '../store/editor.actions';
import { Sprite, Texture } from 'pixi.js';
import { FileRootShape } from '../shape/shapes/general/root/file-root-shape';

@UntilDestroy()
@Component({
  selector: 'iw-canvas',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CanvasComponent implements OnInit {
  @ViewChild('canvas') canvas: ElementRef;

  @ViewChild('canvasContainer') canvasContainer: ElementRef;

  @Input() recordMode = false;

  get overlayConfig() {
    return this.cs.canvasOverlayConfig;
  }

  overlay$: Observable<EditorState['overlay']>;
  isPatchLoading$: Observable<boolean>;
  isFileLoading$: Observable<boolean>;
  isPreviewShapeLoading$: Observable<boolean>;
  relativeAnimationTime$: Observable<number>;
  isStoryBoardOpened$: Observable<boolean>;
  isExportInProgress$: Observable<boolean>;

  bgSelectorPosition$: Observable<{ x: number; y: number; scale: number }>;
  canvasBackgroundColor$: Observable<string>;
  codeMirrorOptions = {
    mode: 'javascript',
    lineNumbers: true,
  };
  currentId: string;
  localStore: Record<string, { resource: ResourceData }> = {};
  command = false;
  dragState: { x: number; y: number };
  showComponentSearch$: Observable<boolean>;
  commentToShow$: Observable<{
    IRI: string;
    position: { x: number; y: number };
    comments: ExtendedComment[];
  }>;
  get previewShape() {
    return this.cs.previewShape;
  }
  get serviceDragState() {
    return this.shapeService.dragState;
  }
  set previewShape(shape: RootShape) {
    this.cs.previewShape = shape;
  }
  get showAlignIcons() {
    return (
      this.shapeService.showVerticalEvenSpacing ||
      this.shapeService.showHorizontalEvenSpacing
    );
  }
  get translated() {
    if (!this.previewShape?.currentMatrix) {
      return { x: undefined, y: undefined };
    }
    const [x, y] = [
      this.previewShape.currentMatrix.e,
      this.previewShape.currentMatrix.f,
    ];
    const scale = this.previewShape.currentMatrix.a;
    return {
      x: (this.cs.canvasX - x) / scale,
      y: (this.cs.canvasY - y) / scale,
      cx: this.cs.canvasX,
      cy: this.cs.canvasY,
      tx: x,
      ty: y,
      scale: scale,
    };
  }

  imageUploadState: string;

  @Input()
  async onFileDropped(event: any) {
    this.imageUploadState = 'in-progress';
    const { files, coords } = event;
    const file = files[0];
    this.cs.uploadeFile = file;

    console.log('------- set-uploade-file -------', this.cs.uploadeFile);
    // -- // -- // -- //

    const extension = file.name.split('.').pop()?.toLowerCase();

    if (file.type.endsWith('svg+xml')) {
      return this.svgParseService.parse(file);
    }

    console.log('coords', coords);

    if (this.cs.previewShape) {
      const { data: base64, error: toBase64Error } =
        await this.cs.toBase64(file);

      const { x, y } = this.cs.getAbsoluteCoords(coords[0], coords[1]);
      const imageShape = this.cs.previewShape.addImageShape([x, y], base64);

      if (toBase64Error) {
        throw new Error(
          `Base64 conversion of the uploaded file resulted an error: ${toBase64Error.message}`,
        );
      }

      // const response = await this.cs.httpService.processImage(base64);
      // console.log('response');
      // return;
      const { error } = await this.cs.httpService.saveImageToS3(
        base64,
        IRIToId(imageShape.IRI),
        extension,
      );

      if (error) {
        throw new Error(
          `Image could not be saved to S3: ${toBase64Error.message}`,
        );
      }
    }
  }

  clickCanvas() {
    // -- //
    // console.log('click-canvas');
    // (this.cs.previewShape as FileRootShape).gsapAnimate();
  }

  openCanvasBgColorEdit() {
    this.store.dispatch(openCanvasBgColorEdit());
  }

  onRightClick(event: MouseEvent) {
    // -- // -- //
    event.stopPropagation();
    event.preventDefault();

    if (this.cs.isControlPressed) {
      return;
    }
    // console.log('right-click', this.cs.draggedSVGElement, {
    //   isControlPressed: this.cs.isControlPressed,
    // });

    if (
      this.shapeService.areThereSelectedShapes() ||
      this.cs.draggedSVGElement
    ) {
      const clickedShape = this.cs.draggedSVGElement?.parent as GeneralShape;

      // TODO - RC's and PC's config should ha a shape parent
      const shapesInScope = this.shapeService.selectedShapes;
      // clickedShape?.selected
      // ? this.shapeService.selectedShapes
      // : [clickedShape];
      this.cs.clickAbsorbed = true;
      this.cs.draggedSVGElement?.stopDrag();
      this.cs.draggedSVGElement = null;
      this.cs.rightClickMenu = {
        x: event.clientX,
        y: event.clientY,
        options: [
          { label: 'Mask shapes', shortcut: 'Shift + m', id: 'mask' },
          { label: 'Create layer from selection', id: 'layer' },
          { label: 'Create component', shortcut: 'Shift + g', id: 'layer' },
        ],
        handler: (id: string) => {
          switch (id) {
            case 'mask':
              this.cs.notification = 'Please select a shape for the masking!';
              this.shapeService.shapesTobeMasked = shapesInScope;
              break;
            case 'layer':
              // this.cs.notification = 'Please select a shape for the masking!';
              // this.shapeService.shapesTobeMasked = shapesInScope;
              break;
            case 'component':
              this.shapeService.createImportedShapeFromSelection();
              break;
          }
          this.cs.rightClickMenu = null;
        },
      };
    } else {
      // -- //
    }
  }

  constructor(
    public readonly cs: CanvasService,
    private readonly viewportRuler: ViewportRuler,
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly ngZone: NgZone,
    public readonly shapeService: ShapeService,
    private readonly svgParseService: SVGParseService,
    private cdr: ChangeDetectorRef,
  ) {
    this.isFileLoading$ = this.store.select(selectIsFileLoading);
    this.isPreviewShapeLoading$ = this.store.select(selectPreviewShapeLoading);

    this.isPreviewShapeLoading$.subscribe(val => {
      // console.log('--------- isPreviewShapeLoading ---------', val);
    });

    this.isExportInProgress$ = this.store.select(selectExportIsInProgress);

    this.relativeAnimationTime$ = this.store.select(
      selectRelativeAnimationTime,
    );

    this.overlay$ = this.store.select(selectOverlay);

    this.viewportRuler
      .change(50)
      .subscribe(() => this.ngZone.run(() => this.resizeCanvas()));

    this.cs.generalEventSubscribe('refresh', () => this.reInit());
    // this.cs.keyEventSubscribe(
    //   p'Shift+r',
    //   () => {
    //     this.cs.consumeKeyEvent('r');
    //     this.reInit();
    //   },
    //   2,
    // );

    this.commentToShow$ = this.store.select(selectCommentToShow);
    this.commentToShow$.subscribe(comment => {
      // console.log('----- commentToShow -------', comment) //
    });
    this.cs.keyEventSubscribe('Escape', () => {
      this.shapeService.selectorRectangle?.remove();
      this.dragState = null;
    });

    this.bgSelectorPosition$ = this.store.select(selectCanvasPosition).pipe(
      map(({ x, y, scale }) => {
        return {
          x,
          y,
          scale: scale.x,
        };
      }),
      catchError(e => {
        console.log('catch-error', e);
        return of(null);
      }),
    );

    this.canvasBackgroundColor$ = this.store.select(
      selectCanvasBackgroundColor,
    );

    this.isPatchLoading$ = this.cs.store.select(isPatchLoading);
  }
  ngOnInit() {
    this.showComponentSearch$ = this.store.select(showComponentSearch);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const { scrollWidth, scrollHeight, offsetLeft, offsetTop } =
        this.canvasContainer.nativeElement;

      this.cs.canvasNativeElement = this.canvas.nativeElement;
      this.cs.canvas = this.canvas;
      this.canvas.nativeElement.appendChild(this.cs.app.view);
      this.cs.resizeCanvas(scrollWidth, scrollHeight);
      this.cs.fitCanvas();
      return;
    });
  }

  scaleBufferCnt = 0;
  scaleBuffer = 0;
  // newShapeIcon
  cursorPos: { x: number; y: number } | null = null;

  onCanvasMouseMove(event: MouseEvent) {
    if (this.cs.shapeAddMode) {
      this.cursorPos = { x: event.clientX, y: event.clientY };
    } else {
      this.cursorPos = null;
    }
  }

  onCanvasMouseEnter() {
    // console.log('on-canvas-mouse-enter');
  }

  onCanvasMouseLeave() {
    this.cursorPos = null;
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.preventDefault();

    /* console.log(
      'dX',
      event.deltaX,
      'dY',
      event.deltaY,
      'dMode',
      event.deltaMode,
    ); */

    if (this.cs.isSpacePressed) {
      this.cs.consumeKeyEvent('Space');
    }

    const { deltaX, deltaY, offsetX, offsetY } = event;

    const { abs } = Math;
    if (abs(deltaX) === 0 || abs(deltaY) === 0) {
      // that is the mouse operation
      if (this.cs.isMetaPressed || deltaX % 1 != 0 || deltaY % 1 != 0) {
        // console.log('meta', { deltaX, deltaY });
        // if the shift is pressed then the deltaX and the deltaY is reveresed
        // console.log('scale-1');
        return this.cs.xscale(offsetX, offsetY, -deltaY);
      }

      // console.log('translate-1');

      // return this.cs.isSpacePressed
      //   ? this.cs.translateCanvas(-deltaY, -deltaX)
      //   : this.cs.translateCanvas(-deltaX, -deltaY);
    }

    if (deltaX % 1 == 0 && deltaY % 1 == 0) {
      // console.log('translate-2');
      return this.cs.translateCanvas(
        -1.5 * deltaX * this.cs._canvasScale,
        -1.5 * deltaY * this.cs._canvasScale,
      );
      // return this.cs.translateCanvas(
      //   (abs(deltaX) > abs(deltaY) ? -2 * deltaX : 0) * this.cs.canvasScale,
      //   (abs(deltaY) > abs(deltaX) ? -2 * deltaY : 0) * this.cs._canvasScale,
      // );
    }
    // console.log('scale-2');

    this.scaleBufferCnt++;
    this.scaleBuffer += deltaY;
    // -- // -- //
    // if (this.scaleBufferCnt < 2) {
    //   return;
    // }
    // -- // -- //

    console.log('wheel', event.offsetX, event.offsetY, event.deltaY);
    // console.log('deltaY', deltaY);

    this.cs.xscale(offsetX, offsetY, -2 * this.scaleBuffer);
    this.scaleBuffer = 0;
    this.scaleBufferCnt = 0;
    return;
    if (
      (abs(deltaX) < 1 && abs(deltaX) != 0) ||
      (abs(deltaY) < 1 && abs(deltaY) != 0)
    ) {
      this.cs.xscale(offsetX, offsetY, -deltaY);
    } else {
      // if (this.cs.isMetaPressed) {
      //   this.cs.xscale(offsetX, offsetY, -deltaY * 10);
      // } else if (this.cs.isSpacePressed) {
      //   return this.cs.translateCanvas(-deltaY / 2, 0);
      // } else {
      //   return this.cs.translateCanvas(0, -deltaY / 2);
      // }
    }
  }

  // @HostListener('gesturestart', ['$event'])
  // @HostListener('gesturechange', ['$event'])
  // @HostListener('gestureend', ['$event'])
  // onGesture(event: any) {
  //   // this.zoomChange.emit(event.scale);
  //   event.preventDefault();
  // }
  // }
  initialTouchPoints: any;
  initialDistance: number;
  initialAngle: number;

  private calculateDistance(points) {
    const [p1, p2] = points;
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  private calculateAngle(points) {
    const [p1, p2] = points;
    return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
  }

  initializedShapeByDrag: GeneralShape;

  @HostListener('mousedown', ['$event'])
  mouseDown(event: MouseEvent) {
    if (event.button === 2) {
      return;
    }
    if (this.cs.mouseDownAbsorbed) {
      this.cs.mouseDownAbsorbed = false;
      return;
    }

    this.cs.mouseDownFree = true;

    if (this.cs.draggedSVGElement && !this.cs.isPressed('Alt')) {
      return;
    }

    // if (this.cs.isPressed('Alt')) {
    //   this.cs.draggedSVGElement = null;
    //   return;
    // }

    if (this.cs.isPressed('Alt')) {
      this.cs.consumeKeyEvent('Alt');
    }

    // if (this.cs.isPressed(['a', 's', 'd'])) {
    //   if (this.cs.draggedSVGElement) {
    //     return;
    //   }

    //   const { offsetX, offsetY } = event;
    //   this._offsetX = offsetX;
    //   this._offsetY = offsetY;
    //   const { x, y } = this.cs.getAbsoluteCoords(offsetX, offsetY);
    //   this.initializedShapeByDrag = this.shapeService.addPathShape(x, y);
    //   return;
    // }

    if (this.cs.shapeAddMode) {
      this.newShapeResizeDims = null;
      const { offsetX, offsetY } = event;
      this._offsetX = offsetX;
      this._offsetY = offsetY;
      const { x, y } = this.cs.getAbsoluteCoords(offsetX, offsetY);
      switch (this.cs.shapeAddMode) {
        case 'rectangle':
          this.initializedShapeByDrag = this.shapeService.addRectangleShape(
            x,
            y,
          );
          break;
        case 'circle':
          this.initializedShapeByDrag = this.shapeService.addCircleShape(x, y);
          break;
        case 'path':
          this.initializedShapeByDrag = this.shapeService.addPathShape(x, y);
          break;
        case 'text':
          this.initializedShapeByDrag = this.shapeService.addTextShape(x, y);
          break;
        case 'comment':
          this.initializedShapeByDrag = this.shapeService.addCommentShape(x, y);
          break;
        case 'glow':
          this.initializedShapeByDrag = this.shapeService.addGlowShape(x, y);
          break;
      }

      return;
    }

    // @ts-ignore
    if (event.target.parentElement.getAttribute('id') == '_canvas') {
      // console.log('------- ee:starting-dragstate -----------');
      this.dragState = { x: event.offsetX, y: event.offsetY };
      this.shapeService.startSelectorRectangle(event.offsetX, event.offsetY);
    }
  }

  // @HostListener('mouseup', ['$event'])
  // mouseup(event: MouseEvent) {
  //   // @ts-ignore
  // }

  _offsetX = 0;
  _offsetY = 0;

  currentPos: Coords;

  newShapeResizeDims: Coords;

  @HostListener('mousemove', ['$event'])
  onMousemove({ offsetX, offsetY }: MouseEvent) {
    this.currentPos = [offsetX, offsetY];

    if (this.initializedShapeByDrag && !this.cs.isPressed('Alt')) {
      const [width, height] = [
        offsetX - this._offsetX,
        offsetY - this._offsetY,
      ];
      this.newShapeResizeDims = [width, height];
      this.initializedShapeByDrag._resize(
        width / this.cs.canvasScale,
        height / this.cs.canvasScale,
      );
      return;
    }
    if (this.dragState) {
      // console.log('--- mouse-move ---', offsetX, offsetY); // -- // -- //
      let { x, y } = this.dragState;
      const [width, height] = [offsetX - x, offsetY - y];
      if (width < 0) {
        x = x + width;
      }
      if (height < 0) {
        y = y + height;
      }
      this.shapeService.dragSelectorRectangle(x, y, width, height);
    }

    // This broadcasts back the moving to the SVG elements that are dragging
    this.cs.mouseMoveEvent(offsetX, offsetY);
  }

  @HostListener('click', ['$event'])
  async click(e: MouseEvent) {
    const log = false;
    if (log) {
      console.log('canvas.comp > clicked', {
        shapeAddMode: this.cs.shapeAddMode,
        clickAbsorbed: this.cs.clickAbsorbed,
        initializedShapeByDrag: this.initializedShapeByDrag,
        draggingPathSection: this.cs.draggingPathSection,
        draggedSVGElement: this.cs.draggedSVGElement,
        dragState: this.dragState,
      });
    }

    const { x, y } = this.cs.getAbsoluteCoords(e.offsetX, e.offsetY);
    this.cs.clickEvent.next();

    // if (this.shapeService.groupRCDragMode) {
    //   this.shapeService.groupRCDragMode = false;
    //   return;
    // }
    this.cs.rightClickMenu = null;
    this.cs.mouseDownFree = false;

    // if (this.cs.clickAbsorbed) {
    //   this.cs.clickAbsorbed = false;
    //   this.cs.draggedSVGElement?.endDragHandler();
    //   this.cs.draggedSVGElement = null;
    //   console.log('return > 1');
    //   this.dragState = null;
    //   return;
    // }

    // TODO - maybe it is not the most elegant place for it //

    if (this.initializedShapeByDrag) {
      this.initializedShapeByDrag.finishInit(!this.newShapeResizeDims);
      this.initializedShapeByDrag = null;
      this.cs.shapeAddMode = null;
      this.cs.draggedSVGElement?.endDragHandler([x, y]);
      // console.log('return > 2');
      return;
    }

    if (this.cs.draggingPathSection && !this.cs.isPressed('Alt')) {
      // As we finish the dragging initial drag session of a path-section another svg element
      // may have been clicked and its drag process would go on. We eliminate it stopDrag
      // #notsupercleancode
      console.log('ee:draggingPathSection-return');
      this.cs.draggedSVGElement?.stopDrag();
      this.cs.draggedSVGElement?.endDragHandler([x, y]);
      this.cs.draggedSVGElement = null;
      this.cs.draggingPathSection?.clickHandler();

      // This is bit dirty but necessary.
      // If the initial drag session is over, then we will click one other element which
      // causes a next drag session to be created. The next line eliminates that
      // console.log('return > 3');
      return;
    }

    if (this.cs.draggedSVGElement) {
      // this is the case when there's for instance an image //

      // console.log('dragged-svg-element > endDragHandler', e.offsetX, e.offsetY);
      this.cs.draggedSVGElement.endDragHandler([x, y]);
      this.cs.draggedSVGElement = null;

      this.cs._hoveredControlPoint?.draggedOnto(
        this.cs.draggedPointController?.config?.controlPoint,
      );
      this.cs.draggedPointController = null;
      // console.log('return > 4'); //

      return;
    }

    // if (this.cs.clickAbsorbed) {
    //   this.cs.clickAbsorbed = false;
    //   this.dragState = null;
    //   return;
    // }
    if (this.dragState) {
      if (
        this.shapeService.selectorWidth <= 1 &&
        this.shapeService.selectorHeight <= 1
      ) {
        const { x, y } = this.shapeService.selectorRectangle;
        this.previewShape?.storyboardController?.checkClick(x, y);
      }
      this.shapeService.removeSelectorRectangle();
      this.dragState = null;
      return;
    }
  }

  click2() {
    // console.log('cs-click > 2');
  }

  handleFiles(files: FileList) {
    const file = files.item(0);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const url = e.target?.result as string;
      const texture = Texture.from(url);
      const sprite = new Sprite(texture);
      // position / add to your Pixi stage or a specific container
      sprite.anchor.set(0.5);
      sprite.x = this.cs._canvasWidth / 2;
      sprite.y = this.cs._canvasHeight / 2;
      this.cs.app.stage.addChild(sprite);
    };
    reader.readAsDataURL(file);
  }

  @HostListener('window:keydown', ['$event'])
  keyEventDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.command = true;
    }
  }

  resizeCanvas() {
    const { scrollWidth, scrollHeight } = this.canvasContainer.nativeElement;
    // TODO - check if it necessary
    // console.log('resize', { scrollWidth, scrollHeight });
    console.log('--------- canvas.comp > resize canvas ----------');
    this.cs.resizeCanvas(scrollWidth, scrollHeight);
  }

  // This routine is called when a file is opened at the side-bar //

  async reInit() {}
}
function isStoryBoardOpened(state: object): boolean {
  throw new Error('Function not implemented.');
}
